import * as React from 'react';
import { connect } from 'react-redux';
import * as Redux from 'redux';
import _ from 'lodash';
import OlSourceVector from 'ol/source/vector';
import OlFeature from 'ol/feature';
import OlLayerVector from 'ol/layer/vector';
import OlStyleStyle from 'ol/style/style';
import OlStyleStroke from 'ol/style/stroke';
import OlFormatWKT from 'ol/format/wkt';
import { translate } from "react-i18next";
import { getSelectInteractionManager } from '../../../../../utilities/selectInteractionManager';
import { getDrawInteractionManager } from '../../../../../utilities/drawInteractionManager';
import { selectFaultById, finishDrawingFaultPhoto, getFaultPhotosAndVideos, getFaults } from '../../../../../actions/faults';
import OlInteractionDraw from 'ol/interaction/draw';
import messageManager from '../../../../../utilities/messageManager';
import loading from '../../../../../utilities/loading';
import { getPhotoFaults, getPhotos } from '../../../../../actions/photos';

const PHOTO_FAULTS_LAYER_NAME = 'PhotoFaults';

export class PhotoFaultsLayer extends React.Component {
    photoFaultsSource = null;
    displayedFeatures = [];

    constructor(props) {
        super(props);
        this.photoFaultsSource = new OlSourceVector();
        const photoFaultsLayer = new OlLayerVector({
            source: this.photoFaultsSource,
            zIndex: 1,
            visible: true,
            name: PHOTO_FAULTS_LAYER_NAME,
            style: new OlStyleStyle({
                stroke: new OlStyleStroke({
                    color: '#ff0000',
                    width: 3
                })
            })
        });
        props.olMap.addLayer(photoFaultsLayer);
        this.addPhotoFaultSelectFeatureInteraction();
    }

    addPhotoFaultSelectFeatureInteraction() {
        const selectConfig = {
            style: new OlStyleStyle({
                stroke: new OlStyleStroke({
                    color: '#2edcd2',
                    width: 3
                })
            }),
            idColumnName: 'id',
            layerName: PHOTO_FAULTS_LAYER_NAME,
            onFeatureSelect: this.onPhotoFaultFeatureSelect.bind(this),
        };
        getSelectInteractionManager('tiles').addSelectConfig(selectConfig);
    }

    onPhotoFaultFeatureSelect(id) {
        const { selectFaultById, photoFaults } = this.props;       
        const photoFault = photoFaults.find(photoFault => photoFault.faultId === id)        
        selectFaultById(photoFault.faultId);
    }

    selectPhotoFaultFeature() {
        const { selectedFault } = this.props;
        if (selectedFault) {
            const selectedFeature = this.displayedFeatures.find(feature => {
                return feature.get('id') === selectedFault.id;
            });
            getSelectInteractionManager('tiles').selectFeature(PHOTO_FAULTS_LAYER_NAME, selectedFeature);
        } else {
            getSelectInteractionManager('tiles').selectFeature(PHOTO_FAULTS_LAYER_NAME, null);
        }
    }

    updatePhotoFaultsFeatures() {
        const { photoFaults } = this.props;
        this.displayedFeatures = [];
        this.photoFaultsSource.clear();

        this.displayedFeatures = photoFaults
            .filter(photoFault => photoFault.geomWkt)
            .map(photoFault => {
                const format = new OlFormatWKT();
                const feature = format.readFeature(photoFault.geomWkt);
                feature.set('id', photoFault.faultId);
                feature.set('layerName', PHOTO_FAULTS_LAYER_NAME);
                this.photoFaultsSource.addFeature(feature);
                return feature;
            });
    }

    startDrawing() {
        const { olMap, finishDrawingFaultPhoto, t } = this.props;

        getDrawInteractionManager('tiles').startDrawing(olMap, 'Circle', OlInteractionDraw.createBox(), (geom) => {
            if(this.props.drawingFaultPhoto !== 'canceled'){
                const newFeature = new OlFeature();
                newFeature.set('layerName', PHOTO_FAULTS_LAYER_NAME);
                this.photoFaultsSource.addFeature(newFeature);
                newFeature.setGeometry(geom);
                this.photoFaultsSource.refresh();

                const format = new OlFormatWKT();
                const geomWkt = format.writeGeometry(geom);
                this.addFaultPhoto(geomWkt)
            } else {
                messageManager.show({
                    type: 'info',
                    text: t('Canceled')
                });
            }
            finishDrawingFaultPhoto();
        });
    }

    finishDrawing() {
        const { olMap } = this.props;
        getDrawInteractionManager('tiles').finishDrawing(olMap, true);
    }

    addFaultPhoto(geomWkt) {
        const { BASE_URL, t, axiosInstance, chosenFlight, selectedFault, selectedPhotoId, getFaultPhotosAndVideos, getPhotoFaults, getPhotos, getFaults } = this.props;
        loading.start();
        axiosInstance.post(`${BASE_URL}fault-photos`,
            {
                faultId: selectedFault.id,
                photoId: selectedPhotoId,
                geomWkt: geomWkt
            })
            .then((result) => {
                getFaults(axiosInstance);
                getPhotos(axiosInstance, chosenFlight ? chosenFlight.id : 0);
                getFaultPhotosAndVideos(axiosInstance, selectedFault.id);
                getPhotoFaults(axiosInstance, selectedPhotoId);
                messageManager.show({
                    type: 'info',
                    text: t('Photo mark created')
                });
                loading.stop();
            })
            .catch((error) => {
                messageManager.show({
                    type: 'error',
                    text: t(error.response.data ? error.response.data : t('Error occured while creating photo mark')),
                });
                loading.stop();
            });
    }

    componentDidMount() {
        this.updatePhotoFaultsFeatures();
        this.selectPhotoFaultFeature();
    }

    componentWillUnmount() {
        const { olMap } = this.props;
        getDrawInteractionManager('tiles').finishDrawing(olMap, false);
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.photoFaults, this.props.photoFaults)) {
            this.updatePhotoFaultsFeatures();
            this.selectPhotoFaultFeature();
        }
        else if (!_.isEqual(prevProps.selectedFault, this.props.selectedFault)) {
            this.selectPhotoFaultFeature();
        }
        if (prevProps.drawingFaultPhoto === this.props.drawingFaultPhoto) return;
        if (this.props.drawingFaultPhoto === 'finished') {
            this.finishDrawing(false);
        } else if (this.props.drawingFaultPhoto === 'canceled') {
            this.updatePhotoFaultsFeatures();
            this.selectPhotoFaultFeature();
            this.finishDrawing(true);
        } else if (this.props.drawingFaultPhoto) {
            this.startDrawing();
        }
    }

    render() {
        return (null);
    }
};

const mapStateToProps = state => ({
    axiosInstance: state.auth.get('axiosInstance'),
    drawingFaultPhoto: state.faults.get('drawingFaultPhoto'),
    selectedFault: state.faults.get('selectedFault'),
    selectedPhotoId: state.photos.get('selectedPhotoId'),  
    chosenFlight: state.flightManager.get('chosenFlight'),  
    photoFaults: state.photos.get('photoFaults'),
    BASE_URL: state.domainProperties.get('BASE_URL'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
    selectFaultById,
    finishDrawingFaultPhoto,
    getPhotos,
    getFaults,
    getFaultPhotosAndVideos,
    getPhotoFaults,
}, dispatch);

export default translate("translations")(connect(mapStateToProps, mapDispatchToProps)(PhotoFaultsLayer));


// WEBPACK FOOTER //
// ./src/containers/content/grid/tilesGalleryWrapper/tiledImageMap/PhotoFaultsLayer.jsx